import { signal, type Signal } from '@preact/signals'
import { useLayoutEffect, useRef } from 'preact/hooks'
import { Icon } from './icon.tsx'
import { useScope } from './scope.tsx'
import { duration } from './tokens.ts'
import './said.css'

interface Note { text: string; tone: 'error' | 'undo' | 'note' | 'done'; action?: { label: string; run(): void } }
export interface Feedback { note: Signal<Note | undefined>; copied: Signal<boolean>; clear(): void; say(text: string, tone?: Note['tone'], action?: Note['action']): void; copy(text: string): Promise<boolean> }
/** A place owns its feedback and deadlines. Navigation is an explicit shell event. */
export function useSaid(): Feedback {
  const scope = useScope(), feedback = useRef<Feedback>()
  if (!feedback.current) {
    const note = signal<Note>(), copied = signal(false)
    let cancel = () => {}, generation = 0
    const clear = () => { generation++; cancel(); note.value = undefined; copied.value = false }
    const say: Feedback['say'] = (text, tone = 'error', action) => {
      clear(); note.value = { text, tone, action }
      if (tone === 'done') cancel = scope.timeout(clear, duration['copy-confirm'])
    }
    feedback.current = { note, copied, clear, say, async copy(text) {
      generation++; cancel(); copied.value = false; const current = generation
      try {
        if (!navigator.clipboard) throw Error('Clipboard unavailable')
        await navigator.clipboard.writeText(text)
        if (scope.disposed || !scope.active || current !== generation) return false
        copied.value = true; note.value = undefined; cancel = scope.timeout(clear, duration['copy-confirm']); return true
      } catch {
        if (!scope.disposed && scope.active && current === generation) say('Couldn’t copy', 'error', { label: 'Retry', run: () => { void feedback.current!.copy(text) } })
        return false
      }
    } }
  }
  useLayoutEffect(() => {
    const value = feedback.current!, navigation = scope.listen(window, 'cx:navigation', value.clear), activity = scope.onActive(active => { if (!active) value.clear() })
    return () => { navigation(); activity(); value.clear() }
  }, [])
  return feedback.current
}
export function Said({ feedback }: { feedback: Feedback }) {
  const note = feedback.note.value
  return note ? <div class="ui-said type-footnote" data-tone={note.tone} role={note.tone === 'error' ? 'alert' : 'status'}>
    <Icon name={note.tone === 'error' ? 'alert' : note.tone === 'done' ? 'check' : 'info'} />
    <span>{note.text}</span>{note.action && <button type="button" onClick={note.action.run}>{note.action.label}</button>}
    {note.tone !== 'done' && <button type="button" aria-label="Dismiss feedback" onClick={feedback.clear}><Icon name="close" /></button>}
  </div> : feedback.copied.value ? <span class="ui-said-live" role="status">Copied</span> : null
}
